// src/redux/gameSlice.js
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // --- Dart Game State ---
  result: "",
  dartCount: 0,
  totalScore: 0,
  hitHistory: [], // Array von Treffer-Einträgen: { label, score, x, y }
  currentRound: [], // Treffer der aktuellen Runde (max. 3 Treffer)
  roundHistory: [], // Abgeschlossene Runden: { hits: [...], totalScore }
  undoLoaded: false, // Flag, ob bereits eine Runde aus roundHistory geladen wurde
  isDisabled: false, // ob die Scheibe gerade gesperrt ist

  // --- Stats State ---
  statistics: null, // beliebige Statistik-Daten
  isLoading: false, // Lade-Indikator für Statistik-Abrufe
  minDartRounds: 33, // Standardwert für die minimale Anzahl an Runden (33 Runden = 99 Darts)
};

const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    // --- Dart reducers ---
    updateResult(state, action) {
      state.result = action.payload;
    },

    incrementDartCount(state) {
      state.dartCount += 1;
      // Sperre, wenn wir das minDartRounds * 3 erreicht oder überschritten haben
      if (state.dartCount >= state.minDartRounds * 3) {
        state.isDisabled = true;
      }
    },

    addHit(state, action) {
      // Guard: max. 3 Darts pro Runde
      if (state.currentRound.length >= 3) return;

      const newHit = {
        label: action.payload.label,
        score: Number(action.payload.score),
        x: action.payload.x,
        y: action.payload.y,
      };

      // Score & Round
      state.totalScore += newHit.score;
      state.currentRound.push(newHit);

      if (state.currentRound.length >= 3) {
        state.isDisabled = true;
      }
    },

    completeRound(state) {
      if (state.currentRound.length === 3) {
        const totalScore = state.currentRound.reduce(
          (sum, hit) => sum + hit.score,
          0
        );

        // Always add the current round to roundHistory
        state.roundHistory.unshift({
          hits: [...state.currentRound],
          totalScore,
        });

        // Convert Proxy object to plain JavaScript object for logging
        const plainRoundHistory = JSON.parse(
          JSON.stringify(state.roundHistory)
        );

        // Only clear currentRound if dartCount is less than minDartRounds * 3
        // This ensures the last throws remain visible when dartCount >= minDartRounds * 3
        state.currentRound = [];

        state.undoLoaded = false;
        // Nach Runde auch wieder freigeben, sofern dartCount < minDartRounds * 3
        state.isDisabled = state.dartCount >= state.minDartRounds * 3;
      }
    },

    undoHit(state) {
      // a) Wenn eine Runde aus history geladen ist, entferne daraus
      if (state.undoLoaded) {
        if (state.currentRound.length > 0) {
          const removed = state.currentRound.pop();
          state.totalScore -= removed.score;
          state.hitHistory.shift();
          state.dartCount = Math.max(0, state.dartCount - 1);
        }
        if (state.currentRound.length === 0) {
          state.undoLoaded = false;
        }
      }
      // b) Wenn currentRound leer & history da → lade letzte Runde zurück
      else if (
        state.currentRound.length === 0 &&
        state.roundHistory.length > 0
      ) {
        const last = state.roundHistory.shift();
        state.currentRound = [...last.hits];
        state.undoLoaded = true;
      }
      // c) Standard: lösche letzten Hit
      else if (state.currentRound.length > 0) {
        const removed = state.currentRound.pop();
        state.totalScore -= removed.score;
        state.hitHistory.shift();
        state.dartCount = Math.max(0, state.dartCount - 1);
      }

      // Nach jeder Änderung: isDisabled nur, wenn round voll oder dartCount ≥ minDartRounds * 3
      state.isDisabled =
        state.currentRound.length >= 3 ||
        state.dartCount >= state.minDartRounds * 3;
    },

    clearHistory(state) {
      // Reset aller Dart-Daten
      state.result = "";
      state.dartCount = 0;
      state.totalScore = 0;
      state.hitHistory = [];
      state.currentRound = [];
      state.roundHistory = [];
      state.undoLoaded = false;
      // Freigeben
      state.isDisabled = false;
    },

    // --- Stats reducers ---
    setStatistics(state, action) {
      state.statistics = action.payload;
    },
    setIsLoading(state, action) {
      state.isLoading = action.payload;
    },
    clearStatistics(state) {
      state.statistics = null;
    },
    setMinDartRounds(state, action) {
      state.minDartRounds = action.payload ?? 33; // Use nullish coalescing to default to 33
      // Direkt sperren oder freigeben je nach aktuellem dartCount
      state.isDisabled = state.dartCount >= state.minDartRounds * 3;
    },
  },
});

export const {
  updateResult,
  incrementDartCount,
  dartCount,
  addHit,
  completeRound,
  undoHit,
  clearHistory,

  setStatistics,
  setIsLoading,
  clearStatistics,
  setMinDartRounds,
} = gameSlice.actions;

export default gameSlice.reducer;
